/**
 * Pure helpers for shared button editing.
 * Behavior must match HomeLauncher ButtonAdminModal.
 */

import {
  DEFAULT_ICON_BG,
  HOME_ACTION_ACTION,
  HOME_ACTION_COLLABORATION,
  HOME_ACTION_CONTACT_LIST,
  HOME_ACTION_PHONE,
  HOME_ACTION_REMINDERS,
  HOME_ACTION_EMAIL_REM,
  HOME_ACTION_EXTERNAL_LEGACY,
  HOME_ACTION_FLOW,
  HOME_ACTION_IMAGE_DISPLAY,
  HOME_ACTION_NONE,
  HOME_ACTION_PAGING,
  HOME_ACTION_SIP_PAGING,
  ICON_TYPE_BUILTIN,
  ICON_TYPE_GENERATE,
  ICON_TYPE_IMAGE,
  ICON_TYPE_TEXT,
} from './constants';

/**
 * Derive up to 5 uppercase initials from a button label.
 * @param {string} name
 * @returns {string}
 */
export function deriveInitials(name) {
  if (!name) return '';
  const parts = name.trim().split(/\s+/).slice(0, 3);
  const letters = parts.map((p) => p[0]).join('');
  return letters.toUpperCase().slice(0, 5);
}

/**
 * Normalize stored actionType for editor display.
 * Legacy `external` → `none`. Missing type with flowId → `flow`.
 * @param {string|undefined|null} actionType
 * @param {string|null|undefined} flowId
 * @returns {string}
 */
export function normalizeActionType(actionType, flowId) {
  if (actionType === HOME_ACTION_EXTERNAL_LEGACY) {
    return HOME_ACTION_NONE;
  }
  if (actionType) {
    return actionType;
  }
  return flowId ? HOME_ACTION_FLOW : HOME_ACTION_NONE;
}

/**
 * Build initial editor values from a HomeLauncher icon/button object.
 * @param {object|null|undefined} icon
 * @returns {object}
 */
export function buildInitialEditorValues(icon) {
  return {
    label: icon?.label || '',
    flowId: icon?.flowId || '',
    iconType:
      icon?.iconType === ICON_TYPE_GENERATE
        ? ICON_TYPE_IMAGE
        : icon?.iconType || ICON_TYPE_BUILTIN,
    iconKey: icon?.iconKey || '',
    iconText: icon?.iconText || '',
    iconBg: icon?.iconBg || DEFAULT_ICON_BG,
    iconImageAssetId: icon?.iconImageAssetId || '',
    actionType: normalizeActionType(icon?.actionType, icon?.flowId),
    actionId: icon?.actionId || '',
    pagingZoneId: icon?.pagingZoneId || '',
    micDeviceId: icon?.micDeviceId || '',
    sipPagingZoneId: icon?.sipPagingZoneId || '',
    imageDisplayActionId: icon?.imageDisplayActionId || '',
    collaborationWorkspaceId: icon?.collaborationWorkspaceId || '',
    contactListId: icon?.contactListId || '',
    // Touch Panel (Phase 2+) — ignored by HomeLauncher payload builder
    targetScreenId: icon?.targetScreenId || '',
  };
}

/**
 * HomeLauncher canSave rules (exact).
 * @param {object} values
 * @returns {boolean}
 */
function savedIconType(iconType) {
  if (iconType === ICON_TYPE_GENERATE) return ICON_TYPE_IMAGE;
  return iconType || ICON_TYPE_BUILTIN;
}

export function canSaveHomeLauncherButton(values) {
  const label = (values?.label || '').trim();
  if (!label) return false;
  if (savedIconType(values?.iconType) === ICON_TYPE_IMAGE) {
    if (!values?.iconImageAssetId || values?.iconUploadBusy) return false;
  }

  const actionType = values?.actionType;
  if (actionType === HOME_ACTION_PAGING) {
    return !!values?.pagingZoneId;
  }
  if (actionType === HOME_ACTION_SIP_PAGING) {
    return !!values?.sipPagingZoneId;
  }
  if (actionType === HOME_ACTION_IMAGE_DISPLAY) {
    return !!values?.imageDisplayActionId;
  }
  if (actionType === HOME_ACTION_COLLABORATION) {
    return !!values?.collaborationWorkspaceId;
  }
  if (actionType === HOME_ACTION_CONTACT_LIST) {
    return !!values?.contactListId;
  }
  if (actionType === HOME_ACTION_PHONE) {
    return true;
  }
  if (actionType === HOME_ACTION_REMINDERS) {
    return true;
  }
  if (actionType === HOME_ACTION_EMAIL_REM) {
    return true;
  }
  if (actionType === HOME_ACTION_ACTION) {
    return !!values?.actionId;
  }
  if (actionType === HOME_ACTION_FLOW) {
    return true; // flowId optional; visual-only allowed
  }
  return true;
}

/**
 * Serialize editor values to the HomeLauncher onSave payload shape.
 * Field set and nulling rules must match the prior ButtonAdminModal.
 *
 * @param {object} values
 * @param {{ fallbackLabel?: string }} [options]
 * @returns {object}
 */
export function buildHomeLauncherSavePayload(values, options = {}) {
  const actionType = values.actionType;
  const iconType = savedIconType(values.iconType);
  const fallbackLabel = options.fallbackLabel || '';

  return {
    label: (values.label || '').trim(),
    actionType,
    flowId: actionType === HOME_ACTION_FLOW ? (values.flowId || null) : null,
    actionId: actionType === HOME_ACTION_ACTION ? (values.actionId || null) : null,
    pagingZoneId: actionType === HOME_ACTION_PAGING ? (values.pagingZoneId || null) : null,
    micDeviceId: actionType === HOME_ACTION_PAGING ? (values.micDeviceId || null) : null,
    sipPagingZoneId:
      actionType === HOME_ACTION_SIP_PAGING ? (values.sipPagingZoneId || null) : null,
    imageDisplayActionId:
      actionType === HOME_ACTION_IMAGE_DISPLAY
        ? (values.imageDisplayActionId || null)
        : null,
    collaborationWorkspaceId:
      actionType === HOME_ACTION_COLLABORATION
        ? (values.collaborationWorkspaceId || null)
        : null,
    contactListId:
      actionType === HOME_ACTION_CONTACT_LIST
        ? (values.contactListId || null)
        : null,
    iconType,
    iconKey: iconType === ICON_TYPE_BUILTIN ? (values.iconKey || null) : null,
    iconText:
      iconType === ICON_TYPE_TEXT
        ? values.iconText || deriveInitials(values.label || fallbackLabel)
        : null,
    iconBg: iconType === ICON_TYPE_TEXT ? values.iconBg : null,
    iconImageAssetId:
      iconType === ICON_TYPE_IMAGE ? (values.iconImageAssetId || null) : null,
  };
}

/**
 * Filter panel / launcher icon catalog by search term (label, name, id, or category).
 * @param {Array<{id:string,label?:string,name?:string,category?:string}>} catalog
 * @param {string} search
 * @returns {Array}
 */
export function filterLauncherIcons(catalog, search) {
  const term = (search || '').trim().toLowerCase();
  if (!term) return catalog || [];
  return (catalog || []).filter((it) => {
    const label = (it.label || it.name || '').toLowerCase();
    const id = (it.id || '').toLowerCase();
    const category = (it.category || '').toLowerCase();
    return label.includes(term) || id.includes(term) || category.includes(term);
  });
}

/** @deprecated Use filterLauncherIcons — kept as an explicit panel alias. */
export const filterPanelIcons = filterLauncherIcons;
